import {
  createEntityAdapter,
  createSelector,
  EntityState,
} from '@reduxjs/toolkit';
import { apiSlice } from './api';
import { ResponseBody } from '../types/common';
import { RootState } from './store';
import { Resource, ResourcesReqParams } from '../types/drive';

const driveAdapter = createEntityAdapter({
  selectId: (resourceList: any) => resourceList.resource.resourceId,
});
const initialState = driveAdapter.getInitialState();

export const driveApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    initDrive: builder.query<ResponseBody, void>({
      query: () => ({
        url: 'library/archive/init',
      }),
      transformResponse: (res: ResponseBody) => res.data,
    }),

    getResources: builder.query<EntityState<Resource>, ResourcesReqParams>({
      query: ({
        folderId,
        page = 0,
        size = 24,
        sortBy = 'reg_date',
        sortDir = 'DESC',
      }) => ({
        url: 'library/archive/resources',
        params: { folderId, page, size, sortBy, sortDir },
      }),
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page && arg.page > 0) {
          currentCache.ids.push(...newItems.ids);
          currentCache.ids = [...new Set(currentCache.ids)];
          currentCache.entities = {
            ...currentCache.entities,
            ...newItems.entities,
          };
        } else {
          return newItems;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      transformResponse: (res: ResponseBody) =>
        driveAdapter.setAll(initialState, res.data.resourceList),
      providesTags: (result, error, arg) =>
        result
          ? [
              'Drive',
              ...result.ids.map((id) => ({
                type: 'Drive' as const,
                id,
              })),
            ]
          : ['Drive'],
    }),
    getResource: builder.query({
      query: (resourceId) => `library/archive/resources/${resourceId}`,
    }),
    validateDuplication: builder.mutation({
      query: (data) => ({
        url: 'library/archive/upload/validate',
        method: 'POST',
        body: { data },
      }),
    }),
    uploadLink: builder.mutation({
      query: (data) => ({
        url: `library/archive/upload/web`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Main', 'Settings'],
    }),
    submitResource: builder.mutation({
      query: (data) => ({
        url: 'library/archive/upload/submit',
        method: 'POST',
        body: { data },
      }),
    }),
    delResource: builder.mutation({
      query: (data) => ({
        url: 'library/archive/resources',
        method: 'DELETE',
        body: { data },
      }),
      invalidatesTags: ['Main', 'Trash', 'Settings'],
    }),
    updateResourceNote: builder.mutation({
      query: ({ resourceId, data }) => ({
        url: `library/archive/resources/${resourceId}/note`,
        method: 'PUT',
        body: { data },
      }),
      invalidatesTags: (result, error, { resourceId }) => [
        { type: 'Drive', id: resourceId },
      ],
    }),
    updateResource: builder.mutation({
      query: ({ projectId, data }) => ({
        url: `library/archive/resources/${projectId}/resource`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { resourceId }) => [
        { type: 'Drive', id: resourceId },
      ],
    }),
    updateUrl: builder.mutation({
      query: ({ projectId, data }) => ({
        url: `archive/detail/resources/${projectId}/url`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { resourceId }) => [
        { type: 'Drive', id: resourceId },
      ],
    }),
    getDriveSharedLink: builder.query({
      query: (resourceId) =>
        `library/archive/resources/${resourceId}/share/new`,
      transformResponse: (res: ResponseBody) => res.data,
    }),
    addDriveSharing: builder.mutation({
      query: ({ projectId, data }) => ({
        url: `library/archive/resources/${projectId}/share`,
        method: 'POST',
        body: { data },
      }),
      invalidatesTags: (result, error, { projectId }) => [
        { type: 'Drive', id: projectId },
      ],
    }),
    deleteDriveSharingLink: builder.mutation({
      query: (resourceId) => ({
        url: `library/archive/resources/${resourceId}/share`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useInitDriveQuery,
  useGetResourcesQuery,
  useGetResourceQuery,
  useValidateDuplicationMutation,
  useUploadLinkMutation,
  useSubmitResourceMutation,
  useDelResourceMutation,
  useUpdateResourceNoteMutation,
  useUpdateResourceMutation,
  useUpdateUrlMutation,
  useGetDriveSharedLinkQuery,
  useAddDriveSharingMutation,
  useDeleteDriveSharingLinkMutation,
} = driveApiSlice;

export const selectDriveResult = driveApiSlice.endpoints.getResources.select({
  page: 0,
});

const selectResourceData = createSelector(
  selectDriveResult,
  (driveResult) => driveResult.data
);

export const {
  selectAll: selectAllResource,
  selectById: selectResourceById,
  selectIds: selectResourceIds,
  selectTotal: selectTotalResourceNum,
} = driveAdapter.getSelectors(
  (state: RootState) => selectResourceData(state) ?? initialState
);
